import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { format } from 'date-fns';
import { it, enUS } from 'date-fns/locale';
import { 
  ChevronDown, ChevronUp, ArrowLeft, Trash2, Edit, 
  AlertCircle, Save, X 
} from 'lucide-react';
import SidebarLayout from '../../components/layout/SidebarLayout';
import { useTranslation } from 'react-i18next';

const StandaloneWorkoutHistory = ({ onBack, userId }) => {
    const { currentUser } = useAuth();
    const { t, i18n } = useTranslation();
    const [allenamenti, setAllenamenti] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedId, setExpandedId] = useState(null);
    const [serieCompletate, setSerieCompletate] = useState({});
    
    // Stati per la modifica
    const [editingSerie, setEditingSerie] = useState(null);
    const [editValues, setEditValues] = useState({ peso: 0, ripetizioni: 0 });
    
    // Stati per i dialoghi di conferma
    const [deletingAllenamento, setDeletingAllenamento] = useState(null);
    const [deletingSerie, setDeletingSerie] = useState(null);

    const userIdToUse = userId || currentUser?.user_id;
    const dateLocale = i18n.language === 'it' ? it : enUS;

    useEffect(() => {
        if (userIdToUse) {
            fetchAllenamenti();
        }
    }, [userIdToUse]);

    const fetchAllenamenti = async () => {
        try {
            setLoading(true);
            const response = await fetch(`/api/get_allenamenti_standalone.php?user_id=${userIdToUse}`);
            const data = await response.json();
            
            if (data.success) {
                const completedWorkouts = data.allenamenti.filter(workout => 
                    workout.durata_totale !== null
                );
                setAllenamenti(completedWorkouts);
            } else {
                throw new Error(data.message || t('workout.errors.loadError'));
            }
        } catch (err) {
            console.error(t('workout.errors.loadError'), err);
            setError(t('workout.errors.loadError'));
        } finally {
            setLoading(false);
        }
    };

    const fetchSerieAllenamento = async (allenamentoId) => {
        try {
            const response = await fetch(`/api/get_completed_series_standalone.php?allenamento_id=${allenamentoId}`);
            const data = await response.json();
            
            if (data.success) {
                setSerieCompletate(prev => ({
                    ...prev,
                    [allenamentoId]: data.serie
                }));
            } else {
                throw new Error(data.message || t('workout.errors.loadExercisesError'));
            }
        } catch (error) {
            console.error(t('workout.errors.loadExercisesError'), error);
            setError(t('workout.errors.loadExercisesError'));
        }
    };

    const toggleExpand = async (id) => {
        setExpandedId(expandedId === id ? null : id);
        if (!serieCompletate[id] && id !== null) {
            await fetchSerieAllenamento(id);
        }
    };

    const handleEditSerie = (serie) => {
        setEditingSerie(serie);
        setEditValues({ 
            peso: parseFloat(serie.peso) || 0, 
            ripetizioni: parseInt(serie.ripetizioni) || 0 
        });
    };


const handleDeleteAllenamento = async (allenamentoId) => {
    try {
        const response = await fetch('/api/delete_allenamento_standalone.php', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ allenamento_id: allenamentoId })
        });
        
        // Controlla prima il tipo di contenuto
        const contentType = response.headers.get("content-type");
        if (!contentType || !contentType.includes("application/json")) {
            throw new Error("Server response was not JSON");
        }
        
        const data = await response.json();
        
        if (data.success) {
            setAllenamenti(prev => prev.filter(a => a.id !== allenamentoId));
            setDeletingAllenamento(null);
            // Opzionale: mostra un messaggio di successo
            // alert(t('workout.messages.deleteSuccess'));
        } else {
            throw new Error(data.message);
        }
    } catch (error) {
        console.error('Errore eliminazione allenamento:', error);
        setError(t('workout.errors.deleteWorkoutError'));
    }
};

const handleDeleteSerie = async (serieId, allenamentoId) => {
    try {
        const response = await fetch('/api/delete_completed_series.php', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ serie_id: serieId })
        });
        
        // Controlla prima il tipo di contenuto
        const contentType = response.headers.get("content-type");
        if (!contentType || !contentType.includes("application/json")) {
            throw new Error("Server response was not JSON");
        }
        
        const data = await response.json();
        
        if (data.success) {
            // Ricarica le serie dell'allenamento
            await fetchSerieAllenamento(allenamentoId);
            setDeletingSerie(null);
        } else {
            throw new Error(data.message);
        }
    } catch (error) {
        console.error('Errore eliminazione serie:', error);
        setError(t('workout.errors.deleteSeriesError'));
    }
};

const handleSaveEdit = async () => {
    try {
        const response = await fetch('/api/update_completed_series.php', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                serie_id: editingSerie.id,
                peso: editValues.peso,
                ripetizioni: editValues.ripetizioni
            })
        });
        
        // Controlla prima il tipo di contenuto
        const contentType = response.headers.get("content-type");
        if (!contentType || !contentType.includes("application/json")) {
            throw new Error("Server response was not JSON");
        }
        
        const data = await response.json();
        
        if (data.success) {
            // Ricarica le serie dell'allenamento
            await fetchSerieAllenamento(editingSerie.allenamento_id);
            setEditingSerie(null);
        } else {
            throw new Error(data.message);
        }
    } catch (error) {
        console.error('Errore aggiornamento serie:', error);
        setError(t('workout.errors.updateSeriesError'));
    }
};

    const groupSerieByEsercizio = (serie) => {
        if (!serie || !Array.isArray(serie)) return {};
        
        const grouped = {};
        serie.forEach(s => {
            const exerciseId = s.esercizio_id || Math.floor(s.serie_number / 100);
            const exerciseName = s.esercizio_nome || `${t('workout.exercise')} ${exerciseId}`;
            
            if (!grouped[exerciseName]) {
                grouped[exerciseName] = [];
            }
            
            const serieWithRealNumber = {
                ...s,
                serieNumber: s.real_serie_number || (s.serie_number % 100)
            };
            
            grouped[exerciseName].push(serieWithRealNumber);
        });
        return grouped;
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return '';
        
        try {
            const date = new Date(dateString);
            const formatString = i18n.language === 'it' 
                ? "EEEE d MMMM yyyy 'alle' HH:mm"
                : "EEEE, MMMM d, yyyy 'at' HH:mm";
            return format(date, formatString, { locale: dateLocale });
        } catch (e) {
            return dateString;
        }
    };

    const formatDuration = (minutes) => {
        if (!minutes) return '0m';
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        if (hours === 0) return `${mins}m`;
        return `${hours}h ${mins}m`;
    };

    const renderContent = () => {
        if (loading) {
            return (
                <div className="flex justify-center items-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 dark:border-blue-400"></div>
                </div>
            );
        }

        if (error) {
            return (
                <div className="p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg">
                    <p className="text-red-700 dark:text-red-300">{error}</p>
                </div>
            );
        }

        if (allenamenti.length === 0) {
            return (
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg shadow p-6 text-center">
                    <p className="text-gray-600 dark:text-gray-300">{t('workout.noWorkoutHistory')}</p>
                </div>
            );
        }

        return (
            <div className="space-y-4">
                {allenamenti.map(allenamento => (
                    <div key={allenamento.id} 
                        className="bg-white dark:bg-gray-800 rounded-lg shadow transition-colors">
                        <div className="p-4">
                            <div className="flex justify-between items-center">
                                <div
                                    onClick={() => toggleExpand(allenamento.id)}
                                    className="flex-1 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 rounded p-2"
                                >
                                    <div className="flex justify-between items-center">
                                        <div>
                                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                                {allenamento.scheda_nome || t('workout.workout')}
                                            </h3>
                                            <p className="text-sm text-gray-600 dark:text-gray-300">
                                                {formatDateTime(allenamento.data_allenamento)}
                                            </p>
                                        </div>
                                        <div className="flex items-center gap-4">
                                            {allenamento.durata_totale && (
                                                <span className="text-sm text-gray-500 dark:text-gray-400">
                                                    {t('workout.duration')}: {formatDuration(allenamento.durata_totale)}
                                                </span>
                                            )}
                                            {expandedId === allenamento.id ? 
                                                <ChevronUp className="h-5 w-5" /> : 
                                                <ChevronDown className="h-5 w-5" />
                                            }
                                        </div>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setDeletingAllenamento(allenamento)}
                                    className="ml-2 p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                                    title={t('common.delete')}
                                >
                                    <Trash2 className="h-5 w-5" />
                                </button>
                            </div>

                            {expandedId === allenamento.id && (
                                <div className="border-t border-gray-200 dark:border-gray-700 mt-4 pt-4">
                                    {!serieCompletate[allenamento.id] ? (
                                        <div className="flex justify-center py-4">
                                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 dark:border-blue-400"></div>
                                        </div>
                                    ) : serieCompletate[allenamento.id].length === 0 ? (
                                        <p className="text-center text-gray-500 dark:text-gray-400">
                                            {t('workout.noExercisesFound')}
                                        </p>
                                    ) : (
                                        Object.entries(groupSerieByEsercizio(serieCompletate[allenamento.id])).map(([esercizioNome, serie]) => (
                                            <div key={esercizioNome} className="mb-6">
                                                <h4 className="font-semibold text-lg mb-3 text-gray-900 dark:text-white">
                                                    {esercizioNome}
                                                </h4>
                                                <div className="overflow-x-auto">
                                                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                                        <thead className="bg-gray-50 dark:bg-gray-900">
                                                            <tr>
                                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                                                    {t('workout.set')}
                                                                </th>
                                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                                                    {t('workout.weight')} (kg)
                                                                </th>
                                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                                                    {t('workout.reps')}
                                                                </th>
                                                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                                                    {t('common.actions')}
                                                                </th>
                                                            </tr>
                                                        </thead>
                                                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                                            {serie.map((s, idx) => (
                                                                <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                                                                        {s.real_serie_number || (s.serie_number % 100) || (idx + 1)}
                                                                    </td>
                                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                                                                        {editingSerie?.id === s.id ? (
                                                                            <input
                                                                                type="number"
                                                                                value={editValues.peso}
                                                                                onChange={(e) => setEditValues({...editValues, peso: parseFloat(e.target.value)})}
                                                                                className="w-20 px-2 py-1 border rounded dark:bg-gray-700 dark:border-gray-600"
                                                                                step="0.5"
                                                                            />
                                                                        ) : (
                                                                            s.peso
                                                                        )}
                                                                    </td>
                                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                                                                        {editingSerie?.id === s.id ? (
                                                                            <input
                                                                                type="number"
                                                                                value={editValues.ripetizioni}
                                                                                onChange={(e) => setEditValues({...editValues, ripetizioni: parseInt(e.target.value)})}
                                                                                className="w-20 px-2 py-1 border rounded dark:bg-gray-700 dark:border-gray-600"
                                                                            />
                                                                        ) : (
                                                                            s.ripetizioni
                                                                        )}
                                                                    </td>
                                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                                                                        {editingSerie?.id === s.id ? (
                                                                            <div className="flex justify-end gap-2">
                                                                                <button
                                                                                    onClick={handleSaveEdit}
                                                                                    className="p-1 text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 rounded"
                                                                                    title={t('common.save')}
                                                                                >
                                                                                    <Save className="h-4 w-4" />
                                                                                </button>
                                                                                <button
                                                                                    onClick={() => setEditingSerie(null)}
                                                                                    className="p-1 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-900/20 rounded"
                                                                                    title={t('common.cancel')}
                                                                                >
                                                                                    <X className="h-4 w-4" />
                                                                                </button>
                                                                            </div>
                                                                        ) : (
                                                                            <div className="flex justify-end gap-2">
                                                                                <button
                                                                                    onClick={() => handleEditSerie(s)}
                                                                                    className="p-1 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded"
                                                                                    title={t('common.edit')}
                                                                                >
                                                                                    <Edit className="h-4 w-4" />
                                                                                </button>
                                                                                <button
                                                                                    onClick={() => setDeletingSerie({ serie: s, allenamentoId: allenamento.id })}
                                                                                    className="p-1 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                                                                                    title={t('common.delete')}
                                                                                >
                                                                                    <Trash2 className="h-4 w-4" />
                                                                                </button>
                                                                            </div>
                                                                        )}
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    // Dialogo di conferma eliminazione allenamento
   if (deletingAllenamento) {
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 max-w-md w-full">
                <div className="flex items-center mb-4">
                    <AlertCircle className="h-8 w-8 text-red-500 mr-3" />
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                        {t('workout.confirmDeleteWorkout')}
                    </h3>
                </div>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                    {t('workout.deleteWorkoutWarning', { 
                        name: deletingAllenamento.scheda_nome,
                        date: formatDateTime(deletingAllenamento.data_allenamento)
                    })}
                </p>
                <div className="flex justify-end gap-3">
                    <button
                        onClick={() => setDeletingAllenamento(null)}
                        className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                        {t('common.cancel')}
                    </button>
                    <button
                        onClick={() => handleDeleteAllenamento(deletingAllenamento.id)}
                        className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg"
                    >
                        {t('common.delete')}
                    </button>
                </div>
            </div>
        </div>
    );
}

    // Dialogo di conferma eliminazione serie
if (deletingSerie) {
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 max-w-md w-full">
                <div className="flex items-center mb-4">
                    <AlertCircle className="h-8 w-8 text-red-500 mr-3" />
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                        {t('workout.confirmDeleteSeries')}
                    </h3>
                </div>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                    {t('workout.deleteSeriesWarning')}
                </p>
                <div className="flex justify-end gap-3">
                    <button
                        onClick={() => setDeletingSerie(null)}
                        className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                        {t('common.cancel')}
                    </button>
                    <button
                        onClick={() => handleDeleteSerie(deletingSerie.serie.id, deletingSerie.allenamentoId)}
                        className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg"
                    >
                        {t('common.delete')}
                    </button>
                </div>
            </div>
        </div>
    );
}

    // Se viene usato come componente indipendente
    if (!onBack) {
        return (
            <SidebarLayout>
                <div className="max-w-2xl mx-auto p-4">
                    <h2 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">
                        {t('workout.workoutHistory')}
                    </h2>
                    {renderContent()}
                </div>
            </SidebarLayout>
        );
    }

    // Se viene usato come componente integrato
    return (
        <div className="max-w-2xl mx-auto p-4">
            <button
                onClick={onBack}
                className="mb-6 px-4 py-2 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 flex items-center gap-1"
            >
                <ArrowLeft className="h-4 w-4" />
                <span>{t('workout.backToWorkout')}</span>
            </button>
            
            <h2 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">
                {t('workout.workoutHistory')}
            </h2>

            {renderContent()}
        </div>
    );
};

export default StandaloneWorkoutHistory;